import React, { FC } from "react";
import {
  Typography,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Box,
  Zoom,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import {
  AccessibleBoard,
  switchState,
} from "../../features/player/playerSlice";
import { isBoardBlockedByIncompatibility } from "../../features/player/playerHelper";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { admin } from "../../features/session/sessionSlice";

import { useAppDispatch, useAppSelector } from "../../store";

interface RegisterSelectBoardProps {
  board: AccessibleBoard;
}

export const RegisterSelectBoard: FC<RegisterSelectBoardProps> = (
  props: RegisterSelectBoardProps
) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const isAdmin = useAppSelector(admin);
  const { board } = props;
  const player = useAppSelector((state) => state.player.player);
  const incompatibleWithSelection = useAppSelector((state) =>
    isBoardBlockedByIncompatibility(board.id as string, state.player)
  );
  const participation = player?.participation?.find(
    (p) => p.tableauId === board.id
  );
  const isCheckedIn = participation?.checkedIn;

  const tournamentContactMail = tournoi?.mail?.trim() ?? "";

  const accent =
    isDark ? theme.palette.secondary.main : theme.palette.primary.main;
  const accentSoft = isDark
    ? theme.palette.secondary.light
    : theme.palette.primary.light;

  const isSelected = board.selected && !board.disabled;
  const isInteractive = !board.disabled && !isCheckedIn;

  /** Texte principal sur la carte : en mode sombre + sélectionné, le fond est une teinte sombre — il faut du clair, pas getContrastText(sur orange plein). */
  const mainText = board.disabled
    ? isDark
      ? theme.palette.grey[100]
      : theme.palette.grey[900]
    : isSelected && isDark
      ? theme.palette.grey[50]
      : isSelected
        ? theme.palette.getContrastText(
            alpha(
              isDark ? theme.palette.secondary.main : theme.palette.primary.main,
              0.35
            )
          )
        : theme.palette.text.primary;

  const mutedText = board.disabled
    ? isDark
      ? alpha(theme.palette.grey[100], 0.75)
      : alpha(theme.palette.grey[900], 0.72)
    : isSelected && isDark
      ? alpha(theme.palette.grey[50], 0.82)
      : isSelected
        ? alpha(mainText, 0.82)
        : isDark
          ? theme.palette.text.secondary
          : theme.palette.grey[700];

  const iconTint = board.disabled
    ? mutedText
    : isSelected && isDark
      ? alpha(theme.palette.secondary.light, 0.95)
      : isSelected
        ? alpha(accent, 0.9)
        : alpha(accentSoft, isDark ? 0.85 : 0.95);

  const categoryWellBg = isDark
    ? alpha(accent, 0.08)
    : alpha(theme.palette.primary.main, 0.06);

  return (
    <Grid2 xs={12} sm={6} md={4} sx={{ display: "flex" }}>
      <Zoom in>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            elevation={0}
            sx={{
              flex: 1,
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              opacity: board.disabled ? 0.85 : 1,
              pointerEvents: board.disabled ? "none" : "auto",
              borderRadius: 2.5,
              border: "1px solid",
              borderStyle: isInteractive && !isSelected ? "dashed" : "solid",
              borderColor: isSelected
                ? alpha(accent, isDark ? 0.95 : 0.55)
                : isInteractive
                  ? alpha(accent, isDark ? 0.42 : 0.38)
                  : alpha(theme.palette.divider, isDark ? 0.45 : 0.85),
              borderLeftWidth: 4,
              borderLeftStyle: "solid",
              borderLeftColor: isSelected
                ? accent
                : alpha(accent, isDark ? 0.45 : 0.5),
              backgroundColor:
                board.disabled && board.selected
                  ? isDark
                    ? alpha(theme.palette.warning.main, 0.2)
                    : alpha(theme.palette.warning.main, 0.16)
                  : board.disabled
                    ? isDark
                      ? theme.palette.grey[900]
                      : theme.palette.grey[200]
                    : isSelected
                      ? isDark
                        ? alpha(theme.palette.secondary.main, 0.18)
                        : alpha(theme.palette.primary.main, 0.12)
                      : isDark
                        ? alpha(theme.palette.grey[900], 0.85)
                        : alpha(theme.palette.background.paper, 1),
              color: mainText,
              boxShadow: isDark
                ? `0 1px 0 ${alpha("#fff", 0.04)} inset, 0 12px 40px ${alpha("#000", 0.35)}`
                : `0 1px 0 ${alpha("#fff", 0.8)} inset, 0 10px 28px ${alpha(theme.palette.common.black, 0.06)}`,
              transition: theme.transitions.create(
                [
                  "transform",
                  "box-shadow",
                  "border-color",
                  "background-color",
                  "border-style",
                ],
                { duration: theme.transitions.duration.shorter }
              ),
              "@media (hover: hover)": {
                "&:hover": {
                  transform: board.disabled ? "none" : "translateY(-2px)",
                  borderStyle: board.disabled ? undefined : "solid",
                  borderColor: board.disabled
                    ? undefined
                    : isSelected
                      ? alpha(accent, isDark ? 1 : 0.65)
                      : alpha(accent, isDark ? 0.85 : 0.62),
                  borderLeftColor: accent,
                  boxShadow: board.disabled
                    ? undefined
                    : isDark
                      ? `0 1px 0 ${alpha("#fff", 0.06)} inset, 0 0 0 1px ${alpha(accent, 0.22)}, 0 16px 48px ${alpha("#000", 0.45)}`
                      : `0 1px 0 ${alpha("#fff", 0.9)} inset, 0 0 0 1px ${alpha(accent, 0.2)}, 0 14px 36px ${alpha(theme.palette.common.black, 0.09)}`,
                },
              },
              ...(isSelected && {
                boxShadow: `${isDark ? `0 0 0 1px ${alpha(accent, 0.45)}` : ""}, 0 12px 40px ${alpha(accent, isDark ? 0.28 : 0.15)}`,
              }),
            }}
          >
            <CardActionArea
              onClick={() => dispatch(switchState(board.id))}
              disabled={board.disabled || isCheckedIn}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                height: "100%",
                textAlign: "left",
                cursor:
                  board.disabled || isCheckedIn ? "default" : "pointer",
                "&.Mui-focusVisible .MuiCardActionArea-focusHighlight": {
                  opacity: isInteractive ? 0.12 : 0,
                },
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  p: 2.25,
                  "&:last-child": { pb: 2.25 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ flexShrink: 0, minWidth: 0 }}
                >
                  <Typography
                    component="div"
                    variant="subtitle1"
                    noWrap
                    title={`Tableau ${board.name}`}
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      fontWeight: 700,
                      fontSize: "1.08rem",
                      letterSpacing: -0.02,
                      lineHeight: 1.35,
                      color: mainText,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        letterSpacing: 0.04,
                        textTransform: "uppercase",
                        color: mutedText,
                        mr: 0.75,
                      }}
                    >
                      Tableau
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 800,
                        color: isSelected ? accentSoft : accent,
                      }}
                    >
                      {board.name}
                    </Box>
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                    sx={{ flexShrink: 0 }}
                  >
                    {isInteractive && !isSelected && (
                      <RadioButtonUncheckedIcon
                        aria-hidden
                        sx={{
                          fontSize: 26,
                          color: alpha(accent, isDark ? 0.75 : 0.65),
                          opacity: 0.92,
                        }}
                      />
                    )}
                    {isCheckedIn && (
                      <Chip
                        label="Pointé"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                        sx={{ fontWeight: 600, zIndex: 2 }}
                      />
                    )}
                    {incompatibleWithSelection && (
                      <Chip
                        label="Incompatible"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600, zIndex: 2 }}
                      />
                    )}
                    {isSelected && (
                      <Chip
                        size="small"
                        label="Sélectionné"
                        sx={{
                          fontWeight: 700,
                          bgcolor: accent,
                          color: theme.palette.getContrastText(accent),
                          border: "none",
                        }}
                      />
                    )}
                  </Stack>
                </Stack>

                {(board.ferme || board.isFull) && !participation && (
                  <Box sx={{ mt: 1.5, flexShrink: 0 }}>
                    <Chip
                      label={
                        <Box component="span" sx={{ display: "block" }}>
                          <Box
                            component="span"
                            sx={{ display: "block", fontWeight: 700, mb: 0.2 }}
                          >
                            Tableau complet
                          </Box>
                          {!isAdmin && (
                            <Box component="span" sx={{ display: "block" }}>
                              {board.hasWaitingQueue
                                ? "Liste d'attente par mail à l'adresse "
                                : "Contactez-nous par mail à l'adresse "}
                              {tournamentContactMail ? (
                                <a
                                  href={`mailto:${tournamentContactMail}`}
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ color: "inherit", textDecoration: "underline" }}
                                >
                                  {tournamentContactMail}
                                </a>
                              ) : (
                                "de contact du tournoi"
                              )}
                              .
                            </Box>
                          )}
                        </Box>
                      }
                      color="error"
                      size="small"
                      icon={<ErrorOutlineRoundedIcon />}
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        alignItems: "center",
                        fontWeight: 500,
                        border: "1px solid",
                        borderColor: "error.dark",
                        color: "error.contrastText",
                        borderRadius: 2.5,
                        boxShadow: `0 6px 14px ${alpha(theme.palette.error.dark, 0.28)}`,
                        "& .MuiChip-icon": {
                          color: "error.contrastText",
                          fontSize: 18,
                          mt: 0.15,
                          ml: 0.9,
                        },
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                          lineHeight: 1.3,
                          py: 0.65,
                          pr: 1.1,
                        },
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    flex: "1 1 auto",
                    display: "flex",
                    flexDirection: "column",
                    mt: 2,
                    minHeight: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1.25}
                    sx={{
                      flex: "1 1 auto",
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: categoryWellBg,
                      border: `1px solid ${alpha(accent, isDark ? 0.12 : 0.18)}`,
                    }}
                  >
                    <Groups2OutlinedIcon
                      sx={{
                        fontSize: 22,
                        mt: 0.15,
                        flexShrink: 0,
                        color: iconTint,
                        opacity: board.disabled ? 0.55 : 1,
                      }}
                    />
                    <Typography
                      component="div"
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.55,
                        color: board.disabled ? "inherit" : mainText,
                        wordBreak: "break-word",
                      }}
                    >
                      {board.label}
                    </Typography>
                  </Stack>
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    flexShrink: 0,
                    mt: "auto",
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: isSelected
                      ? alpha(mainText, isDark ? 0.2 : 0.15)
                      : alpha(theme.palette.divider, isDark ? 0.5 : 1),
                  }}
                >
                  <AccessTimeOutlinedIcon
                    sx={{
                      fontSize: 20,
                      flexShrink: 0,
                      color: iconTint,
                      opacity: board.disabled ? 0.55 : 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      color: mutedText,
                      fontWeight: 500,
                      lineHeight: 1.35,
                      whiteSpace: "normal",
                    }}
                  >
                    Heure limite de pointage&nbsp;: {board.heureLimite}
                  </Typography>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      </Zoom>
    </Grid2>
  );
};
